import DevUser379 from './DevUser379';
import { createTiles } from '../../common/utils/TileUtils';

/**
 * Mock states for the OffersCard docs chapter. Each entry supplies the exact
 * WelcomeCardContext values needed to force a specific NotificationSection or
 * PromotionSection state without hitting the GraphQL endpoints.
 */

// getAccrualData() only matches the accrual entry for the current year.
const CURRENT_YEAR = new Date().getFullYear();

const DEFAULT_PRO_XTRA_SPEND = {
  totalCurrentYearSpend: '15000',
  totalCurrentYearSavings: '750',
};

const createUserData = ({
  proXtraSpend, perkSummary, customerInfo, proTiers, accrual
} = {}) => ({
  ...DevUser379,
  proXtraSpend: {
    ...DevUser379.proXtraSpend,
    ...DEFAULT_PRO_XTRA_SPEND,
    ...proXtraSpend,
  },
  perkSummary: {
    ...DevUser379.perkSummary,
    ...perkSummary,
    accrualData: [
      { ...DevUser379.perkSummary.accrualData[0], year: CURRENT_YEAR, ...accrual },
    ],
  },
  customerInfo: {
    ...DevUser379.customerInfo,
    ...customerInfo,
  },
  proTiers: {
    currentTierName: 'Member',
    benefitTags: [],
    ...proTiers,
  },
});

const createAvailablePerksData = (overrides = {}) => ({
  availablePerks: 0,
  expiringPerks: 0,
  availableOffers: 0,
  expiringOffers: 0,
  daysLeft: 0,
  challengeTitles: [],
  sweepstakes: [],
  ...overrides,
});

const createMockState = ({ isAdmin, userData, availablePerksData, ...rest }) => {
  const resolvedUserData = userData ?? createUserData();
  const resolvedPerksData = availablePerksData ?? createAvailablePerksData();
  const tiles = createTiles({
    isAdmin,
    userData: resolvedUserData,
    availablePerksData: resolvedPerksData,
    isLoadingPerks: false,
    availablePerksError: false,
    refreshAvailablePerks: () => {},
    refreshAvailableOffers: () => {},
    showSweepstakesTile: false,
    sweepstakesData: {},
    componentName: 'OffersCard',
    ...rest
  });

  return {
    isAdmin,
    isLoading: false,
    userDataError: false,
    isLoadingPerks: false,
    availablePerksError: false,
    showSweepstakesTile: false,
    sweepstakesData: {},
    userData: resolvedUserData,
    availablePerksData: resolvedPerksData,
    tiles,
    ...rest,
  };
};

const NOT_FINAL_TIER_ACCRUAL = {
  finalTierPerkAchieved: false,
  spendForNextPerk: 25000,
  spendForNextPerkWithPxcc: 6250,
};

const SWEEPSTAKES_TILE_DATA = {
  title: 'Enter for a chance to win a Pro Xtra Giveaway',
  image: 'Pro-Xtra_Perk_Icon-whiteFill.svg',
  totalEntriesAvailable: 4,
  totalEntriesCompleted: 1,
};

export const OFFERS_CARD_MOCK_STATES = [
  {
    id: 'spend-no-spend-no-savings',
    label: 'Spend summary: No spend, no savings',
    mockData: createMockState({
      isAdmin: true,
      userData: createUserData({ proXtraSpend: { totalCurrentYearSpend: '0', totalCurrentYearSavings: '0' } }),
    }),
  },
  {
    id: 'spend-no-savings',
    label: 'Spend summary: Spend, no savings',
    mockData: createMockState({
      isAdmin: true,
      userData: createUserData({ proXtraSpend: { totalCurrentYearSavings: '0' } }),
    }),
  },
  {
    id: 'spend-large-amounts',
    label: 'Spend summary: Large spend and savings amounts',
    mockData: createMockState({
      isAdmin: true,
      userData: createUserData({
        proXtraSpend: { totalCurrentYearSpend: '1250000', totalCurrentYearSavings: '87500' },
      }),
    }),
  },
  {
    id: 'spend-six-figure-amounts',
    label: 'Spend summary: Six-figure spend and savings amounts',
    mockData: createMockState({
      isAdmin: true,
      userData: createUserData({
        proXtraSpend: { totalCurrentYearSpend: '999999', totalCurrentYearSavings: '99999' },
      }),
    }),
  },
  {
    id: 'spend-multi-million-amounts',
    label: 'Spend summary: Multi-million spend and savings amounts',
    mockData: createMockState({
      isAdmin: true,
      userData: createUserData({
        proXtraSpend: { totalCurrentYearSpend: '12500000', totalCurrentYearSavings: '1750000' },
      }),
    }),
  },
  {
    id: 'notification-well-done',
    label: 'Notification: Well Done (final tier, no perks)',
    mockData: createMockState({ isAdmin: true }),
  },
  {
    id: 'notification-expiring-perks',
    label: 'Notification: Perks Expiring Soon',
    mockData: createMockState({
      isAdmin: true,
      availablePerksData: createAvailablePerksData({ expiringPerks: 3, daysLeft: 5 }),
    }),
  },
  {
    id: 'notification-unclaimed-perk',
    label: 'Notification: New Perk Unlocked',
    mockData: createMockState({
      isAdmin: true,
      userData: createUserData({ perkSummary: { unclaimedPerksCount: 1 } }),
    }),
  },
  {
    id: 'notification-available-perks',
    label: 'Notification: Perks Available',
    mockData: createMockState({
      isAdmin: true,
      availablePerksData: createAvailablePerksData({ availablePerks: 2 }),
    }),
  },
  {
    id: 'notification-pro-credit-card',
    label: 'Notification: Pro Credit Card progress (PXCC enrolled)',
    mockData: createMockState({
      isAdmin: true,
      userData: createUserData({
        perkSummary: { pxccUserCategory: 'Enrolled', perkCurrentTierProgressPercentage: '45' },
        accrual: NOT_FINAL_TIER_ACCRUAL,
      }),
    }),
  },
  {
    id: 'notification-progress-perk',
    label: 'Notification: Perk progress (PXCC unenrolled)',
    mockData: createMockState({
      isAdmin: true,
      userData: createUserData({
        perkSummary: { pxccUserCategory: 'Unenrolled', perkCurrentTierProgressPercentage: '45' },
        accrual: NOT_FINAL_TIER_ACCRUAL,
      }),
    }),
  },
  {
    id: 'notification-expiring-offers',
    label: 'Notification: Expiring Offers',
    mockData: createMockState({
      isAdmin: true,
      availablePerksData: createAvailablePerksData({ expiringOffers: 2 }),
    }),
  },
  {
    id: 'notification-available-offers',
    label: 'Notification: Available Offers',
    mockData: createMockState({
      isAdmin: true,
      availablePerksData: createAvailablePerksData({ availableOffers: 4 }),
    }),
  },
  {
    id: 'notification-elite-support',
    label: 'Notification: Elite Support Line',
    mockData: createMockState({
      isAdmin: true,
      userData: createUserData({
        proTiers: {
          currentTierName: 'Elite',
          benefitTags: [{ tagName: 'eliteCallCenter', value: '1-877-767-5629' }],
        },
      }),
    }),
  },
  {
    id: 'notification-preferred-unlocked',
    label: 'Notification: Preferred Pricing Unlocked',
    mockData: createMockState({
      isAdmin: true,
      userData: createUserData({ customerInfo: { enrolledInPreferredPricing: true } }),
    }),
  },
  {
    id: 'notification-paint-member',
    label: 'Notification: Paint Member Products',
    mockData: createMockState({
      isAdmin: true,
      userData: createUserData({
        customerInfo: { enrolledInPreferredPricing: false, paintMembershipLevel: 'Gold' },
      }),
    }),
  },
  {
    id: 'notification-sweepstakes-tile',
    label: 'Notification: Sweepstakes tile',
    mockData: createMockState({
      isAdmin: true,
      showSweepstakesTile: true,
      sweepstakesData: SWEEPSTAKES_TILE_DATA,
      availablePerksData: createAvailablePerksData({ sweepstakes: [SWEEPSTAKES_TILE_DATA] }),
    }),
  },
  {
    id: 'notification-challenge-skinny-bar',
    label: 'Notification: Challenge skinny bar',
    mockData: createMockState({
      isAdmin: true,
      availablePerksData: createAvailablePerksData({
        challengeTitles: ['Spend $2,500 to earn a $100 Pro Xtra offer'],
      }),
    }),
  },
  {
    id: 'notification-loading',
    label: 'Notification: Loading skeletons',
    mockData: createMockState({ isAdmin: true, isLoadingPerks: true }),
  },
  {
    id: 'promotion-default',
    label: 'Promotion: Default (non-admin)',
    mockData: createMockState({ isAdmin: false }),
  },
  {
    id: 'promotion-preferred-pricing',
    label: 'Promotion: Preferred Pricing promo',
    mockData: createMockState({
      isAdmin: false,
      userData: createUserData({ customerInfo: { preferredPricingTierZero: true } }),
    }),
  },
  {
    id: 'card-error',
    label: 'Card: Error page',
    mockData: createMockState({ isAdmin: true, userDataError: true }),
  },
  {
    id: 'card-loading',
    label: 'Card: Loading',
    mockData: createMockState({ isAdmin: true, isLoading: true }),
  },
];

export const getOffersCardMockState = (id) => {
  return OFFERS_CARD_MOCK_STATES.find((state) => state.id === id);
};

export const exportsForTesting = {
  createUserData,
  createAvailablePerksData,
  createMockState,
};
